import React, { useState, useEffect } from 'react';
import { PATHS } from '../../router/paths';
import { Link } from 'react-router-dom'
import SwitchOrg from "../SwitchOrg";
import { dispatchCustomEvent } from '../../containers/WorkkardDetail/utils';
import { getNavItem, isTablet } from '../../common/util';
import Icon from '../../icons';
import { useSelector, shallowEqual } from "react-redux";
import QualityAssurance from '../../icons/QualityAssurance';
import ComposeAndSendEmail from '../../icons/ComposeAndSendEmail';
import { parseAuToken, finalizeAuth } from '../../utils/oidcTokenStorage';
import { isAccessTokenExpired, refreshAccessToken } from '../../utils/tokenRefresh';
import { useLegacyHistory } from '../../router/hooks';
import { selectSessionContextOidc } from '@container/AdminPanel/features/users/usersSelectors';

const isNavTabVisible = (nav, oldName) => {
    if (!oldName) return true;
    if (!nav) return true;
    return Boolean(nav[oldName]);
};

const getNavIcon = (nav, oldName, fallbackIcon) => nav?.[oldName]?.Icon || fallbackIcon;

/*Method to check that tab url exist in path name or not*/
const isTabPathExistInPathName = (path, taburl) => path.indexOf(taburl) >= 0;

const parseMercuryPermissions = (perm) => {
    const parsed = perm?.mercury_permissions && JSON.parse(perm.mercury_permissions);
    return parsed?.hasOwnProperty("permissions") ? parsed.permissions : null;
};

const isMercuryPermissionChecked = (perm, permissionName) => {
    const permissions = parseMercuryPermissions(perm);
    return permissions ? !!permissions.find(({ name }) => name === permissionName)?.is_checked : false;
};

const renderTabLi = (ctx, key, {
    id,
    liOnClick,
    anchorOnClick,
    anchorTitle,
    anchorClassName,
    icon,
    label,
    outerClassName,
    extra,
}) => {
    const content = (
        <React.Fragment>
            <div className="leftSection">
                <a title={anchorTitle} className={anchorClassName} onClick={anchorOnClick}>
                    {icon}
                    <span>{label}</span>
                </a>
            </div>
            {extra}
        </React.Fragment>
    );
    return (
        <li key={key} id={id} data-test={`left-nav-${id}`} className={`pointer ${ctx.currentActive == id ? 'active' : null}`} onClick={liOnClick}>
            {outerClassName ? <div className={outerClassName}>{content}</div> : content}
        </li>
    );
};

const TAB_DEFINITIONS = [
    {
        id: 'workkardtab',
        isEnabled: (ctx) => isNavTabVisible(ctx.nav, "workkards") && !!(ctx.userPermission?.can_create_workkard || ctx.userPermission?.can_archive_workkard ||
            ctx.userPermission?.can_change_workkard_sponsor || ctx.userPermission?.can_receive_un_assigned_workkards ||
            ctx.userPermission?.can_close_workkard || ctx.userPermission?.can_reopen_workkard || ctx.userPermission?.can_edit_workkard),
        matchesPath: (pathName, nav) => ["workkards", "workkard/createworkkard", "unassignedworkkards", "crtwrk", "getclmdetail", getNavItem(nav, "workkards")].includes(pathName),
        render: (ctx, key) => renderTabLi(ctx, key, {
            id: 'workkardtab',
            liOnClick: () => ctx.onTabClick("workkardtab", getNavItem(ctx.nav, "workkards")),
            anchorTitle: getNavItem(ctx.nav, "workkards", 1),
            anchorClassName: "workkardIcon",
            icon: <Icon className="icon" name={getNavIcon(ctx.nav, "workkards", "workkard")} />,
            label: getNavItem(ctx.nav, "workkards", 1),
            outerClassName: "rowSpaceBetween growMob",
            extra: (
                <div className="btnAddMob">
                    <Link
                        to={PATHS.WORKKARD_CREATE}
                        state={{ returnUrl: '/workkards' }}
                        className="btn btnAdd buttonGradient addIcon ml"
                        data-test="left-nav-add-workkard"
                        title="Add Workkard"
                    >
                        <Icon name="add" className="icon" />
                    </Link>
                </div>
            ),
        }),
    },
    {
        id: 'activitytab',
        isEnabled: (ctx) => isNavTabVisible(ctx.nav, "activities") && !!(ctx.userPermission?.can_create_activity || ctx.userPermission?.can_edit_activity || ctx.userPermission?.can_reassign_activity || ctx.userPermission?.can_accept_reject_activity
            || ctx.userPermission?.can_close_activity || ctx.userPermission?.can_archive_activity || ctx.userPermission?.can_accept_reject_bid || ctx.userPermission?.can_receive_un_assigned_activities
            || ctx.userPermission?.can_reopen_activity || ctx.userPermission?.CanChangeActivityOwner || ctx.userPermission?.can_close_activity_during_mid_term
            || ctx.userPermission?.can_mark_activity_as_in_progress || ctx.userPermission?.can_assign_b2b),
        matchesPath: (pathName, nav) => ["activities", "cact", "unassignedactivities", "cloneact", "gad", getNavItem(nav, "activities")].includes(pathName),
        render: (ctx, key) => renderTabLi(ctx, key, {
            id: 'activitytab',
            liOnClick: () => ctx.onTabClick("activitytab", getNavItem(ctx.nav, "activities")),
            anchorTitle: getNavItem(ctx.nav, "activities"),
            anchorClassName: "activityIcon",
            icon: <Icon className="icon" name={getNavIcon(ctx.nav, "activities", "activityies")} />,
            label: getNavItem(ctx.nav, "activities", 1),
        }),
    },
    {
        id: 'todotab',
        isEnabled: (ctx) => isNavTabVisible(ctx.nav, "diaries"),
        matchesPath: (pathName, nav) => ["tdo", "ctdo", "dtdo", "etdo", getNavItem(nav, "tdo")].includes(pathName),
        render: (ctx, key) => renderTabLi(ctx, key, {
            id: 'todotab',
            liOnClick: () => ctx.onTabClick("todotab", getNavItem(ctx.nav, "tdo")),
            anchorTitle: getNavItem(ctx.nav, "diaries", 1),
            anchorClassName: "diarydIcon",
            icon: <Icon className="icon" name={getNavIcon(ctx.nav, "diaries", "diaryies")}/>,
            label: getNavItem(ctx.nav, "diaries", 1),
        }),
    },
    {
        id: 'viewtab',
        isEnabled: (ctx) => isNavTabVisible(ctx.nav, "views"),
        matchesPath: (pathName, nav) => ["view", getNavItem(nav, "views")].filter(p => p.startsWith(pathName.split('/')[0])).length > 0,
        render: (ctx, key) => renderTabLi(ctx, key, {
            id: 'viewtab',
            liOnClick: () => ctx.onTabClick("viewtab", PATHS.VIEWS),
            anchorTitle: getNavItem(ctx.nav, "views", 1),
            anchorClassName: "viewsIcon",
            icon: <Icon className="icon" name={getNavIcon(ctx.nav, "views", "views")}/>,
            label: getNavItem(ctx.nav, "views", 1),
        }),
    },
    {
        id: 'inboxtab',
        isEnabled: (ctx) => isNavTabVisible(ctx.nav, "inbox") && !!ctx.userPermission?.can_access_inbox,
        matchesPath: (pathName) => isTabPathExistInPathName(pathName, "inbox"),
        render: (ctx, key) => renderTabLi(ctx, key, {
            id: 'inboxtab',
            liOnClick: () => ctx.onTabClick("workkardtab", "inbox"),
            anchorClassName: "inboxIcon",
            icon: <Icon name={getNavIcon(ctx.nav, "inbox", "inboxIcons")} className="icon" />,
            label: "Inbox",
        }),
    },
    {
        id: 'bulkinboxtab',
        isEnabled: (ctx) => isNavTabVisible(ctx.nav, "bulk inbox") && process.env.show_bulk_inbox == "true" && !!ctx.userPermission?.can_access_bulk_inbox,
        matchesPath: (pathName) => pathName === "bulkinbox",
        render: (ctx, key) => renderTabLi(ctx, key, {
            id: 'bulkinboxtab',
            liOnClick: () => ctx.onTabClick("workkardtab", "bulkinbox"),
            anchorTitle: getNavItem(ctx.nav, "bulkinboxIcon", 1),
            anchorClassName: "bulkinboxIcon",
            icon: <Icon className="icon" name={getNavIcon(ctx.nav, "bulk inbox", "bulkinboxMenu")}/>,
            label: getNavItem(ctx.nav, "Bulk Inbox", 1),
        }),
    },
    {
        id: 'mercurysetab',
        isEnabled: (ctx) => isNavTabVisible(ctx.nav, "desks") && !!ctx.userPermission?.can_access_mercury &&
            process.env.foo_send_email_feature === "true" &&
            isMercuryPermissionChecked(ctx.userPermission, "Can Compose and Send Email"),
        matchesPath: (pathName) => isTabPathExistInPathName(pathName, "mb"),
        render: (ctx, key) => renderTabLi(ctx, key, {
            id: 'mercurysetab',
            liOnClick: () => ctx.onTabClick("mercurysetab", "mb"),
            anchorClassName: "mercuryIcon",
            icon: <ComposeAndSendEmail className="icon"/>,
            label: <span className='composeAndSendEmail'>Mailbox</span>,
        }),
    },
    {
        id: 'mercurytab',
        isEnabled: (ctx) => isNavTabVisible(ctx.nav, "desks") && !!ctx.userPermission?.can_access_mercury,
        matchesPath: (pathName) => isTabPathExistInPathName(pathName, "mercury"),
        render: (ctx, key) => renderTabLi(ctx, key, {
            id: 'mercurytab',
            liOnClick: () => ctx.onTabClick("workkardtab", "mercury"),
            anchorClassName: "mercuryIcon",
            icon: <Icon name={getNavIcon(ctx.nav, "desks", "mercuryIcons")} className="icon" />,
            label: "Mercury",
        }),
    },
    {
        id: 'mercuryqatab',
        isEnabled: (ctx) => isNavTabVisible(ctx.nav, "desks") && !!ctx.userPermission?.can_access_mercury &&
            process.env.foo_qa_feature === "true" && isNavTabVisible(ctx.nav, "review center"),
        matchesPath: (pathName) => isTabPathExistInPathName(pathName, "mrc"),
        render: (ctx, key) => renderTabLi(ctx, key, {
            id: 'mercuryqatab',
            liOnClick: () => ctx.onTabClick("workkardtab", "mrc"),
            anchorClassName: "mercuryIcon",
            icon: <QualityAssurance className="icon"/>,
            label: <span className='qualityAssurance'>Review Center</span>,
        }),
    },
    {
        id: 'mercurydashboard',
        isEnabled: (ctx) => isNavTabVisible(ctx.nav, "desks") && !!ctx.userPermission?.can_access_mercury &&
            isMercuryPermissionChecked(ctx.userPermission, "Can Access Dashboard"),
        matchesPath: (pathName) => isTabPathExistInPathName(pathName, "dashboard"),
        render: (ctx, key) => renderTabLi(ctx, key, {
            id: 'mercurydashboard',
            liOnClick: () => ctx.onTabClick("workkardtab", "mercury/dashboard"),
            anchorClassName: "mercuryIcon",
            icon: <Icon className="icon" name="leaderboardMenu"/>,
            label: getNavItem(ctx.nav, "dashboard", 1),
        }),
    },
    {
        id: 'financialtab',
        isEnabled: (ctx) => isNavTabVisible(ctx.nav, "approvals") && !!ctx.userPermission?.can_access_financials && !ctx.sessionContext.IsAuditUser,
        matchesPath: (pathName) => isTabPathExistInPathName(pathName, "financial"),
        render: (ctx, key) => renderTabLi(ctx, key, {
            id: 'financialtab',
            liOnClick: () => ctx.onTabClick("financialtab", "financial/?ct=approvals"),
            anchorClassName: "approvalIcon",
            icon: <Icon name={getNavIcon(ctx.nav, "approvals", "approvalIcons")} className="icon" />,
            label: "Approvals",
        }),
    },
    {
        id: 'letterstab',
        isEnabled: (ctx) => isNavTabVisible(ctx.nav, "letters") && !!ctx.userPermission?.can_access_letters,
        matchesPath: (pathName) => isTabPathExistInPathName(pathName, "letters"),
        render: (ctx, key) => renderTabLi(ctx, key, {
            id: 'letterstab',
            liOnClick: () => ctx.onTabClick("letterstab", "letters"),
            anchorClassName: "lettersIcon",
            icon: <svg className='icon'><use xlinkHref='/SVG/exzeo2x-icon.svg#letters'></use></svg>,
            label: "Letters",
        }),
    },
    {
        id: 'accountingtab',
        isEnabled: (ctx) => isNavTabVisible(ctx.nav, "accounts") && !!ctx.userPermission?.can_access_accounts,
        render: (ctx, key) => renderTabLi(ctx, key, {
            id: 'accountingtab',
            liOnClick: () => ctx.onTabClick("accountingtab", "accounting"),
            anchorClassName: "approvalIcon",
            icon: <svg className='icon'><use xlinkHref='/SVG/exzeo2x-icon.svg#accounting'></use></svg>,
            label: "Accounting",
        }),
    },
    {
        id: 'aDashboard',
        isEnabled: (ctx) => isNavTabVisible(ctx.nav, "dashboard"),
        render: (ctx, key) => renderTabLi(ctx, key, {
            id: 'aDashboard',
            anchorOnClick: ctx.openDashboard,
            anchorTitle: getNavItem(ctx.nav, "dashboard", 1),
            anchorClassName: "pointer dashboardIcon",
            icon: <Icon className="icon" name={getNavIcon(ctx.nav, "dashboard", "dashboard")}/>,
            label: getNavItem(ctx.nav, "dashboard", 1),
        }),
    },
];

const ACTIVE_TAB_MATCH_ORDER = [
    'workkardtab','activitytab', 'todotab', 'viewtab', 'bulkinboxtab', 'inboxtab',
    'mercurysetab', 'mercuryqatab', 'mercurydashboard', 'mercurytab', 'financialtab', 'letterstab',
];

const computeActiveTab = (pathName, nav) => {
    for (const id of ACTIVE_TAB_MATCH_ORDER) {
        const tab = TAB_DEFINITIONS.find((t) => t.id === id);
        if (tab?.matchesPath?.(pathName, nav)) return id;
    }
    return "";
};

const LeftNav = (props) => {
    const { history } = useLegacyHistory();
    const { orgSuggestions, nav } = useSelector(state => ({
        // headerContext: state.headerContext,
        orgSuggestions: state.header.headerObject.orgSuggestion,
        nav: state.navigation?.data
    }), shallowEqual);
        const sessionContext = useSelector(selectSessionContextOidc);
        const userView = sessionContext?.userView;
        const userPermission = userView?.permissions;
    const [currentActive, setCurrentActive] = useState("");
    const [isOrgPopup, toggleOrgPopup] = useState(false);
    useEffect(() => {
      props.setShowLeftPanel(props.showLeftPanel);
    }, [props.showLeftPanel]);

    useEffect(() => {
        setCurrentActive(setActiveTabsCssClass())
    }, [userView])

    const getOrgLogo = () => {
        var orgView = sessionContext.organizationView;
        return <img src="/images/default_org.png" alt="" />
    }

    const onTabClick = (clickedTab, url) => {
        if(url==='mercury/dashboard')
        {
            let redirectUrl = process.env.mercury_dashboard_url??url;
            window.open(redirectUrl,'_blank');
        }
        else if (clickedTab === "viewtab")
        {
            setCurrentActive(clickedTab)
            props.setShowLeftPanel ? props.setShowLeftPanel(false) : true;
            history.push(PATHS.VIEWS)
        }
        else
        {
            setCurrentActive(clickedTab)
            props.setShowLeftPanel ? props.setShowLeftPanel(false) : true;
                const infinityUrls = ["workkards", getNavItem(nav, "workkards"), "activities", getNavItem(nav, "activities"), "getclmdetail"]
                let pathName = window.location.pathname.toLowerCase().replace(/^\/+/, '');
                if (infinityUrls.includes(pathName) && infinityUrls.includes(url)) {
                    if (pathName == url) {
                        history.push("/temp");
                        history.goBack();
                    } else {
                        history.push(`/${url}`)
                    }
                } else {
                    window.location = `/${url}`
                }
        }

    }

    const openDashboard = () => {
        const isIE = false || !!document.documentMode;
        dispatchCustomEvent(isIE, 'dashboardClicked');
    }

    const setActiveTabsCssClass = () => {
        let pathName = window.location.pathname.toLowerCase().replace(/^\/+/, '');
        return computeActiveTab(pathName, nav);
    }

    // The old "/switchorgview" backend call (CHANGE_USER_ORG_TOKEN,
    // headerSaga.js) belongs to the legacy cc-web app: it writes cc-web's
    // own AuthCookieName/OrgCookieName cookies and redirects to a cc-web
    // URL. The OIDC frontend never reads those cookies (only _au/_co/
    // exzeoid), so that round trip left the session pinned to the old org.
    // Org switching for an already-issued OIDC token works the same way
    // the initial multi-org login screen does it (auth.jsx's
    // OrganizationSelectionScreen): reuse the same access token, just point
    // _co at a different org via finalizeAuth, then do a full navigation
    // to "/" so Auth's bootstrap re-establishes session state (and lands
    // on a sensible default page) for the newly selected org instead of
    // staying on a URL scoped to a resource the old org owned.
    const switchOrgTokenAction = async (orgid) => {
        let tokenResponse = parseAuToken();
        if (!tokenResponse?.access_token) return;
        if (isAccessTokenExpired(tokenResponse.access_token)) {
            try {
                // Rotates both access_token and refresh_token and persists
                // them via persistAuthArtifacts (tokenRefresh.js's
                // doRefresh) before we touch org selection. On failure it
                // already clears the session and redirects to "/" itself
                // (handleRefreshFailure) — nothing further to do here.
                tokenResponse = await refreshAccessToken();
            } catch (e) {
                return;
            }
        }
        finalizeAuth(tokenResponse, { org_id: orgid });
        window.location.href = window.location.origin + '/';
    }

    const ctx = { nav, currentActive, onTabClick, userPermission, sessionContext, openDashboard };
    const visibleTabs = nav ? TAB_DEFINITIONS.filter((tab) => tab.isEnabled(ctx)) : [];

    return (
        <div className="LeftNavMain leftNavSection" style={  isTablet() ? props.showLeftPanel ? { display: "block" }: { display: "none" }: null }  >
        <div className="transparentOverlay"  onClick={() => {
                    props.setShowLeftPanel(false);
                    }}></div>
        <div
            className={`LeftNavMain animated faster ${
            isTablet() ? "fadeInLeft" : ""
            }`}
        >
            <div className="ccLogo quickTooltip-right" data-quicktooltip="Claim Colony" data-test="ccLogo"><a><Icon name="CasheLogo" className="icon ccActive" /></a></div>

            <div className="mainNav" data-test="left-nav">
            <div className="flexGrow wkDetailNumberMob">
                <span className="mr__1 ml__1 mt">
                <Icon
                    name="close"
                    className="icon"
                    onClick={() => {
                    props.setShowLeftPanel(false);
                    }}
                />
                </span>
            </div>
                <ul data-test="left-nav-tabs">
                    {visibleTabs.map((tab, key) => tab.render(ctx, key))}
                </ul>
            </div>

            <div className={isOrgPopup ? "orgProfile quickTooltip-right dropDown active" : "orgProfile quickTooltip-right dropDown"} data-test="switchOrg" data-quicktooltip={(sessionContext && sessionContext.organizationView && !isOrgPopup) ? sessionContext.organizationView.OrganizationName : null}>
                <a onClick={(evt) => {
                    if (orgSuggestions.length > 1) {
                        toggleOrgPopup(!isOrgPopup)
                    }
                    evt.stopPropagation();
                }} >
                    {userView && sessionContext.organizationView && getOrgLogo()}
                </a>
                {isOrgPopup &&
                    <SwitchOrg data={orgSuggestions} switchOrg={switchOrgTokenAction} currentOrgId={sessionContext.organizationView.Id} toggleOrgPopup={toggleOrgPopup} />
                }
            </div>
        </div>
        </div>
    );
}

export default LeftNav;
